import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useLayoutEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { useEffect, useReducer } from 'react';
import { getImages } from './fns/getImages';
const defaultAsyncImageCellState = {
    fromTimestamp: null,
    toTimestamp: null,
    hasImage: null,
    imageUrl: null,
    imageTimestamp: null,
    showPopup: false,
    popupPos: null,
};
const aspectRatio = 4 / 3;
const WidthPixels = 160;
const HeightPixels = Math.round(WidthPixels / aspectRatio);
const AsyncImageCellReducer = (prev, next) => ({ ...prev, ...next });
export const AsyncImageCell = ({ params, client, StreamARN, Fragments }) => {
    const [state, dispatch] = useReducer(AsyncImageCellReducer, defaultAsyncImageCellState);
    const { imageUrl, imageTimestamp, fromTimestamp, toTimestamp, showPopup, popupPos } = state;
    const cellRef = useRef(null);
    useLayoutEffect(() => {
        if (showPopup && cellRef.current) {
            const rect = cellRef.current.getBoundingClientRect();
            dispatch({ popupPos: {
                    top: rect.top + window.scrollY,
                    left: rect.left + window.scrollX,
                    width: rect.width,
                    height: rect.height,
                } });
        }
        else if (!showPopup) {
            dispatch({ popupPos: null });
        }
    }, [showPopup]);
    const asyncGetImages = async ({ row }) => {
        const { FragmentNumber, ProducerTimestamp } = row;
        const nextRowIndex = Fragments.findIndex((r, i) => r.FragmentNumber === FragmentNumber);
        const nextProducerTimestamp = nextRowIndex >= 0 ? Fragments[nextRowIndex + 1]?.ProducerTimestamp?.getTime() : undefined;
        const t1 = ProducerTimestamp?.getTime() || 0;
        const t2 = nextProducerTimestamp || t1 + 1000;
        const fromTimestamp = Math.min(t1, t2);
        let toTimestamp = Math.max(t1, t2);
        if (fromTimestamp === toTimestamp) {
            toTimestamp = fromTimestamp + 1000;
        }
        if (!isNaN(fromTimestamp) && !isNaN(toTimestamp)) {
            try {
                const { Images } = await getImages({ client, StreamARN, fromTimestamp, toTimestamp, MaxResults: 1, dimensions: { WidthPixels, HeightPixels }, SamplingInterval: 200 });
                if (Images) {
                    const firstImage = Images[0];
                    if (firstImage?.ImageContent) {
                        const blob = base64ToBlob(firstImage.ImageContent, 'image/jpeg');
                        const imageUrl = URL.createObjectURL(blob);
                        dispatch({ imageUrl, imageTimestamp: firstImage.TimeStamp?.getTime() || null });
                    }
                    else {
                        dispatch({ imageUrl: null, imageTimestamp: null });
                    }
                }
            }
            catch (e) {
                console.error(`AsyncImageCell: getImages failed for fragment ${FragmentNumber} from ${new Date(fromTimestamp).toISOString()} to ${new Date(toTimestamp).toISOString()}`, e);
            }
        }
    };
    useEffect(() => {
        let cancelled = false;
        asyncGetImages(params);
        return () => {
            cancelled = true;
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
            }
        };
    }, [params]);
    if (imageUrl === null) {
        return _jsx("span", { children: "Loading\u2026" });
    }
    else if (imageUrl) {
        return (_jsxs("div", { ref: cellRef, style: { position: "relative", height: "100%" }, children: [_jsx("img", { src: imageUrl, alt: "Fragment Image", style: {
                        height: "100%",
                        width: "auto",
                        display: "block",
                        objectFit: "contain",
                        cursor: "pointer",
                    }, onMouseEnter: () => dispatch({ showPopup: true }), onMouseLeave: () => dispatch({ showPopup: false }) }), showPopup && popupPos &&
                    ReactDOM.createPortal(_jsx("div", { style: {
                            position: "absolute",
                            top: popupPos.top,
                            left: popupPos.left,
                            width: popupPos.width,
                            height: popupPos.height,
                            pointerEvents: "auto",
                            zIndex: 2000,
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            background: "rgba(255,255,255,0.95)",
                        }, onMouseEnter: () => dispatch({ showPopup: true }), onMouseLeave: () => dispatch({ showPopup: false }), children: _jsx("img", { src: imageUrl, alt: "Full Fragment", style: {
                                maxWidth: "90vw",
                                maxHeight: "90vh",
                                background: "#fff",
                                display: "block",
                            } }) }), document.body)] }));
    }
    else {
        return _jsx("span", { children: "No Image" });
    }
};
function base64ToBlob(base64, mime = 'image/jpeg') {
    const byteString = atob(base64);
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
    }
    return new Blob([ab], { type: mime });
}
